import { api, type QueryParams } from '@/lib/api/client';
import type { I18n, MediaAsset, MediaUsage, Paginated } from '@/lib/api/types';

export const mediaApi = {
  async list(params?: QueryParams): Promise<Paginated<MediaAsset>> {
    const res = await api.getRaw<MediaAsset[]>('/admin/media', params);
    // Server sends `data: null` for an empty list; normalize to [] so the
    // Paginated<T> contract (data: T[]) holds for every consumer.
    return { data: res.data ?? [], meta: res.meta as Paginated<MediaAsset>['meta'] };
  },
  get(id: string): Promise<MediaAsset> {
    return api.get<MediaAsset>(`/admin/media/${id}`);
  },
  usages(id: string): Promise<MediaUsage[]> {
    return api.get<MediaUsage[]>(`/admin/media/${id}/usages`);
  },
  upload(input: {
    file: File;
    alt?: I18n;
    title?: I18n;
    folder?: string;
    restricted?: boolean;
  }): Promise<MediaAsset> {
    const fd = new FormData();
    fd.append('file', input.file);
    if (input.alt) fd.append('alt', JSON.stringify(input.alt));
    if (input.title) fd.append('title', JSON.stringify(input.title));
    if (input.folder) fd.append('folder', input.folder);
    fd.append('restricted', input.restricted ? 'true' : 'false');
    return api.upload<MediaAsset>('/admin/media', fd);
  },
  update(id: string, body: Partial<Pick<MediaAsset, 'alt' | 'title' | 'caption' | 'credit' | 'folder' | 'tags' | 'is_restricted'>>): Promise<MediaAsset> {
    return api.patch<MediaAsset>(`/admin/media/${id}`, body);
  },
  remove(id: string): Promise<null> {
    return api.del(`/admin/media/${id}`);
  },
};

/** Accepted upload kinds for client pre-validation (server checks magic bytes). */
export const ACCEPTED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp', 'gif', 'svg', 'pdf', 'doc', 'docx', 'mp4', 'webm'];
